import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ArrowRight, BookOpen, ChevronRight, Home, Moon, Search, Sun } from 'lucide-react'
import { applyColourScheme } from '@/kit/ui'
import { useDevStore } from '@/store/devStore'
import { DOC_GROUPS, DOC_PAGES } from './pages'

interface TocItem { id: string; text: string; level: number }

/**
 * /docs/:page (C-1b, owner: "a real doc like fakeapi.net"): a documentation site in the portal. Left: the topics in
 * groups with a search; middle: the page (breadcrumb, title, sections, previous / next); right: "On this page", read
 * from the page's headings, the one in view marked. Its own full page, so the text has room; a way back to the Hub.
 */
export function DocsScreen() {
  const { page = 'introduction' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const index = Math.max(0, DOC_PAGES.findIndex((p) => p.slug === page))
  const doc = DOC_PAGES[index]!
  const prev = DOC_PAGES[index - 1]
  const next = DOC_PAGES[index + 1]
  const [query, setQuery] = useState('')
  const [toc, setToc] = useState<TocItem[]>([])
  const [active, setActive] = useState('')
  const article = useRef<HTMLElement>(null)
  const scheme = useDevStore((s) => s.colourScheme)
  const setScheme = useDevStore((s) => s.setColourScheme)

  // an unknown page goes to the first one
  useEffect(() => { if (!DOC_PAGES.some((p) => p.slug === page)) navigate('/docs/introduction', { replace: true }) }, [page, navigate])
  useEffect(() => { document.title = `${doc.title} · Lender Hub docs` }, [doc.title])

  // the index of this page: its headings, after it rendered; then to the heading in the address (or the top)
  useEffect(() => {
    const heads = Array.from(article.current?.querySelectorAll<HTMLElement>('[data-toc]') ?? [])
    setToc(heads.map((h) => ({ id: h.id, text: h.textContent ?? '', level: Number(h.dataset.toc) })))
    setActive(heads[0]?.id ?? '')
    const target = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null
    if (target) target.scrollIntoView?.()
    else window.scrollTo?.(0, 0)
  }, [doc.slug, location.hash])

  // which heading is in view
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !toc.length) return
    const seen = new Map<string, boolean>()
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) seen.set(e.target.id, e.isIntersecting)
      const first = toc.find((t) => seen.get(t.id))
      if (first) setActive(first.id)
    }, { rootMargin: '-64px 0px -65% 0px' })
    for (const t of toc) { const el = document.getElementById(t.id); if (el) io.observe(el) }
    return () => io.disconnect()
  }, [toc])

  const q = query.trim().toLowerCase()
  const shown = useMemo(() => DOC_PAGES.filter((p) => !q || `${p.title} ${p.summary} ${p.keywords} ${p.group}`.toLowerCase().includes(q)), [q])
  const toggleScheme = () => { const n = scheme === 'dark' ? 'light' : 'dark'; applyColourScheme(n); setScheme(n) }
  const Body = doc.Component

  return (
    <div className="min-h-screen bg-white text-[14px] text-default dark:bg-slate-950" data-testid="docs">
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <Link to="/docs/introduction" className="flex items-center gap-2 font-bold text-default no-underline">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-white"><BookOpen size={15} /></span>
          <span>Lender Hub <span className="font-normal text-muted">docs</span></span>
        </Link>
        <div className="relative ml-2 hidden max-w-xs flex-1 md:block">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input className="input w-full pl-8" placeholder="Search the docs…" aria-label="Search the docs" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && shown[0]) { navigate(`/docs/${shown[0].slug}`); setQuery('') } }} />
        </div>
        <div className="flex-1" />
        <Link to="/" className="btn btn-secondary btn-sm"><ArrowLeft size={13} /> Back to the Hub</Link>
        <button type="button" className="icon-btn" onClick={toggleScheme} aria-label={scheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>{scheme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}</button>
      </header>

      <div className="mx-auto flex max-w-[1440px]">
        {/* the topics */}
        <nav className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-64 shrink-0 overflow-y-auto border-r border-slate-200 px-3 py-5 lg:block dark:border-slate-800" aria-label="Docs topics">
          {DOC_GROUPS.map((g) => {
            const items = shown.filter((p) => p.group === g)
            if (!items.length) return null
            return (
              <div key={g} className="mb-5">
                <div className="mb-1.5 px-2 text-2xs font-bold uppercase tracking-wider text-muted">{g}</div>
                <ul className="space-y-0.5">
                  {items.map((p) => (
                    <li key={p.slug}>
                      <NavLink to={`/docs/${p.slug}`} onClick={() => setQuery('')} className={({ isActive }) => `block rounded-md px-2 py-1.5 no-underline transition ${isActive ? 'bg-primary-soft font-semibold text-primary-text' : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900'}`}>{p.title}</NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
          {!shown.length && <p className="px-2 text-xs text-muted">Nothing found for “{query}”.</p>}
        </nav>

        {/* the page */}
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-3xl">
            <select className="input mb-4 w-full lg:hidden" aria-label="Docs page" value={doc.slug} onChange={(e) => navigate(`/docs/${e.target.value}`)}>
              {DOC_GROUPS.map((g) => <optgroup key={g} label={g}>{DOC_PAGES.filter((p) => p.group === g).map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}</optgroup>)}
            </select>
            <nav aria-label="Breadcrumb" className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-muted">
              <Link to="/docs/introduction" aria-label="Docs home" className="text-muted"><Home size={13} /></Link>
              <ChevronRight size={12} />
              <span>{doc.group}</span>
              <ChevronRight size={12} />
              <span className="rounded-full bg-primary-soft px-2 py-0.5 text-primary-text">{doc.title}</span>
            </nav>
            <h1 className="mb-2 text-3xl font-bold tracking-tight">{doc.title}</h1>
            <p className="mb-6 text-muted">{doc.summary}</p>
            <article ref={article} key={doc.slug} data-testid={`doc-${doc.slug}`}>
              <Body />
            </article>
            <div className="mt-12 grid grid-cols-1 gap-3 border-t border-slate-200 pt-6 sm:grid-cols-2 dark:border-slate-800">
              {prev ? (
                <Link to={`/docs/${prev.slug}`} className="rounded-md border border-slate-200 px-4 py-3 no-underline transition hover:border-primary dark:border-slate-700" data-testid="doc-prev">
                  <span className="flex items-center gap-1 text-2xs text-muted"><ArrowLeft size={12} /> Previous</span>
                  <span className="font-semibold text-default">{prev.title}</span>
                </Link>
              ) : <span />}
              {next && (
                <Link to={`/docs/${next.slug}`} className="rounded-md border border-slate-200 px-4 py-3 text-right no-underline transition hover:border-primary dark:border-slate-700" data-testid="doc-next">
                  <span className="flex items-center justify-end gap-1 text-2xs text-muted">Next <ArrowRight size={12} /></span>
                  <span className="font-semibold text-default">{next.title}</span>
                </Link>
              )}
            </div>
            <p className="mt-6 pb-8 text-center text-2xs text-muted">The SDK’s code: <code>firstfund-lender-hub/sdk</code> · these pages: <code>portal/src/screens/docs</code></p>
          </div>
        </main>

        {/* on this page */}
        <aside className="sticky top-14 hidden h-[calc(100vh-3.5rem)] w-60 shrink-0 overflow-y-auto border-l border-slate-200 px-4 py-6 xl:block dark:border-slate-800" aria-label="On this page">
          <div className="mb-2 text-2xs font-bold uppercase tracking-wider text-muted">On this page</div>
          <ul className="space-y-1 text-[12.5px]">
            {toc.map((t) => (
              <li key={t.id} className={t.level === 3 ? 'pl-3' : ''}>
                <a href={`#${t.id}`} onClick={(e) => { e.preventDefault(); navigate({ hash: t.id }, { replace: true }); setActive(t.id) }} className={`block border-l-2 py-0.5 pl-2 no-underline ${active === t.id ? 'border-primary font-medium text-primary-text' : 'border-transparent text-muted hover:text-default'}`}>{t.text}</a>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  )
}
